import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { compact, uniq } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class RouteValidationService {
  private reservedRoutes: string[] = [];

  constructor(private router: Router) {
    this.buildReservedRoutesList();
  }

  /**
   * Build list of reserved routes from router configuration
   */
  private buildReservedRoutesList(): void {
    const routes = this.router.config;
    const reservedPaths: string[] = [];

    // Process main routes
    routes.forEach((route) => {
      if (route.path) {
        const topLevelPath = this.extractTopLevelPath(route.path);
        if (topLevelPath && !topLevelPath.includes(':')) {
          reservedPaths.push(topLevelPath);
        }
      }

      // Process child routes (like frontend children)
      if (route.children) {
        route.children.forEach((childRoute) => {
          if (childRoute.path) {
            const topLevelPath = this.extractTopLevelPath(childRoute.path);
            if (topLevelPath && !topLevelPath.includes(':')) {
              reservedPaths.push(topLevelPath);
            }
          }
        });
      }
    });

    // Remove duplicates and empty strings
    this.reservedRoutes = compact(uniq(reservedPaths));

    console.log('Reserved routes detected:', this.reservedRoutes);
  }

  /**
   * Extract the top-level segment from a route path
   */
  private extractTopLevelPath(path: string): string {
    if (!path || path === '') return '';

    // Handle special cases
    if (path === '**') return ''; // Wildcard route
    if (path.startsWith(':')) return ''; // Parameter route like :id

    // Extract first segment before any parameters or slashes
    const segments = path.split('/');
    const firstSegment = segments[0];

    // Skip parameter routes
    if (firstSegment.includes(':')) return '';

    return firstSegment;
  }

  /**
   * Check if a permalink is valid for root level usage
   */
  isValidRootLevelPermalink(permalink: string): boolean {
    if (!permalink || permalink.trim() === '') {
      return false;
    }

    // Clean the permalink (remove ~ if present)
    const cleanPermalink = permalink.startsWith('~')
      ? permalink.substring(1)
      : permalink;

    // Check against reserved routes
    return !this.reservedRoutes.includes(cleanPermalink.toLowerCase());
  }

  /**
   * Get list of reserved routes (for displaying to user)
   */
  getReservedRoutes(): string[] {
    return [...this.reservedRoutes];
  }

  /**
   * Get validation error message
   */
  getValidationErrorMessage(permalink: string): string {
    const cleanPermalink = permalink.startsWith('~')
      ? permalink.substring(1)
      : permalink;

    if (this.reservedRoutes.includes(cleanPermalink.toLowerCase())) {
      return `The permalink "${cleanPermalink}" conflicts with an existing route and cannot be used at root level.`;
    }

    return '';
  }

  /**
   * Suggest alternative permalinks
   */
  suggestAlternatives(permalink: string): string[] {
    const cleanPermalink = permalink.startsWith('~')
      ? permalink.substring(1)
      : permalink;
    const suggestions: string[] = [];

    // Add common suffixes
    const suffixes = ['page', 'event', '2024', 'info'];

    suffixes.forEach((suffix) => {
      const suggestion = `${cleanPermalink}-${suffix}`;
      if (this.isValidRootLevelPermalink(suggestion)) {
        suggestions.push(suggestion);
      }
    });

    return suggestions.slice(0, 3); // Return max 3 suggestions
  }
}
